import { useMemo } from "react";
import type { Batch, BatchReport, BulkBatchDecisionResponse, CertificatePreview } from "@/api/client";
import { batchLabel } from "@/components/batchLabel";
import { formatDate } from "@/components/format";
import { EmptyState, Metric, StatusBadge } from "@/components/ui";
import { ScrollArea } from "@/components/ScrollArea";

export function BatchTable({
  batches,
  activeID,
  selected = [],
  onActive,
  onSelected,
}: {
  batches: Batch[];
  activeID: string | null;
  selected?: string[];
  onActive: (id: string) => void;
  onSelected?: (ids: string[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(id: string, checked: boolean) {
    if (!onSelected) return;
    const next = new Set(selectedSet);
    if (checked) next.add(id);
    else next.delete(id);
    onSelected(Array.from(next));
  }

  return (
    <ScrollArea label="Batches">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr>
            {onSelected ? <th className="w-10 px-4 py-3"></th> : null}
            <th className="px-4 py-3 font-medium">Batch</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Recipients</th>
            <th className="px-4 py-3 font-medium">Progress</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {batches.map((batch) => (
            <tr
              key={batch.id}
              onClick={() => onActive(batch.id)}
              className={activeID === batch.id ? "bg-brand-light/15" : "cursor-pointer hover:bg-zinc-50"}
            >
              {onSelected ? (
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(batch.id)}
                    onChange={(e) => toggle(batch.id, e.target.checked)}
                    className="h-4 w-4 rounded border-zinc-300"
                  />
                </td>
              ) : null}
              <td className="px-4 py-3">
                <div className="font-medium text-zinc-950">{batchLabel(batch)}</div>
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={batch.status} />
              </td>
              <td className="px-4 py-3 text-zinc-700">{batch.total_count}</td>
              <td className="px-4 py-3">
                <ProgressBar batch={batch} />
              </td>
              <td className="px-4 py-3 text-zinc-500">{formatDate(batch.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  );
}

export function BatchStats({ batch }: { batch: Batch }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <Metric label="Total" value={batch.total_count} />
      <Metric label="Generated" value={batch.generated_count ?? 0} />
      <Metric label="Failed" value={batch.failed_count ?? 0} tone={batch.failed_count ? "danger" : "default"} />
    </div>
  );
}

export function PreviewTable({ rows, loading }: { rows: CertificatePreview[]; loading: boolean }) {
  if (loading) return <EmptyState title="Loading preview" detail="Rows are being fetched." compact />;
  if (rows.length === 0) return <EmptyState title="No preview rows" detail="This batch has no assigned preview rows yet." compact />;
  return (
    <ScrollArea label="Preview rows" className="max-h-72 overflow-y-auto rounded-md border border-zinc-200">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 border-b border-zinc-200 bg-zinc-50 text-zinc-500">
          <tr>
            <th className="px-3 py-2 font-medium">Row</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Membership</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((row) => (
            <tr key={`${row.row}-${row.full_name}`}>
              <td className="px-3 py-2 text-zinc-500">{row.row}</td>
              <td className="px-3 py-2 font-medium text-zinc-800">{row.full_name}</td>
              <td className="px-3 py-2 font-mono text-zinc-600">{row.membership_no ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  );
}

export function ReportPanel({ report, loading }: { report?: BatchReport; loading: boolean }) {
  if (loading) return <EmptyState title="Loading report" detail="Generation report is being fetched." compact />;
  if (!report) return <EmptyState title="No report yet" detail="A report appears after generation starts." compact />;
  const failures = report.failures ?? [];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Metric label="Reported" value={report.total ?? 0} />
        <Metric label="Done" value={report.generated ?? 0} />
        <Metric label="Failed" value={report.failed ?? 0} tone={report.failed ? "danger" : "default"} />
      </div>
      {failures.length > 0 ? (
        <ScrollArea label="Generation failures" className="max-h-72 overflow-y-auto rounded-md border border-zinc-200">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 border-b border-zinc-200 bg-zinc-50 text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Attempts</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {failures.map((failure) => (
                <tr key={failure.certificate_id}>
                  <td className="px-3 py-2 font-medium text-zinc-800">{failure.full_name}</td>
                  <td className="px-3 py-2 text-zinc-600">{failure.attempts ?? 0}</td>
                  <td className="px-3 py-2 text-zinc-600">{failure.error ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      ) : (
        <EmptyState title="No failed certificates" detail="Failures will be listed here when present." compact />
      )}
    </div>
  );
}

export function BulkResultSummary({
  result,
  names = {},
}: {
  result: BulkBatchDecisionResponse;
  names?: Record<string, string>;
}) {
  const ok = result.results.filter((item) => item.ok).length;
  const failures = result.results.filter((item) => !item.ok);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm">
      <span className="font-medium text-zinc-950">{ok} completed</span>
      <span className="ml-3 text-zinc-500">{failures.length} failed</span>
      {failures.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs">
          {failures.map((item, i) => (
            <li key={`${item.batch_id}-${i}`}>
              <span className="font-medium text-zinc-900">{names[item.batch_id] ?? item.batch_id}</span>
              <span className="ml-2 text-red-700">{item.error?.message ?? "Failed"}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ProgressBar({ batch }: { batch: Batch }) {
  const total = Math.max(batch.total_count, 0);
  const generated = Math.max(batch.generated_count ?? 0, 0);
  const failed = Math.max(batch.failed_count ?? 0, 0);
  const pct = total === 0 ? 0 : Math.min(100, Math.round(((generated + failed) / total) * 100));
  return (
    <div className="min-w-36">
      <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        {generated}/{total} generated
      </div>
    </div>
  );
}
